import axios from "axios";
import config from "../config";

/**
 * @typedef {Object} QuestionImportFailure
 * @property {number|string|null} rowNumber
 * @property {number|string|null} questionCode
 * @property {string} reason
 */

/**
 * @typedef {Object} QuestionImportResult
 * @property {number} totalRows
 * @property {number} createdCount
 * @property {number} updatedCount
 * @property {number} attachedCount
 * @property {number} alreadyAttachedCount
 * @property {number} failedCount
 * @property {QuestionImportFailure[]} failures
 * @property {Array<Object>} importedQuestions
 * @property {Array<string|number>} importedQuestionIds
 * @property {Object} raw
 */

const numberValue = (source, keys) => {
  const key = keys.find((candidate) => source?.[candidate] != null);
  const value = key ? Number(source[key]) : 0;
  return Number.isFinite(value) ? value : 0;
};

const arrayValue = (source, keys) => {
  const key = keys.find((candidate) => Array.isArray(source?.[candidate]));
  return key ? source[key] : [];
};

const normalizeFailure = (failure) => ({
  rowNumber:
    failure?.rowNumber ??
    failure?.excelRowNumber ??
    failure?.row ??
    failure?.RowNumber ??
    null,
  questionCode:
    failure?.questionCode ??
    failure?.qCode ??
    failure?.code ??
    failure?.QuestionCode ??
    null,
  reason:
    failure?.reason ??
    failure?.error ??
    failure?.message ??
    failure?.Reason ??
    "خطای اعتبارسنجی",
});

/**
 * Normalize both canonical and legacy response casing.
 * @param {Object} responseData
 * @returns {QuestionImportResult}
 */
export const normalizeQuestionImportResult = (responseData) => {
  const source = responseData?.data ?? responseData?.result ?? responseData ?? {};
  const failures = arrayValue(source, [
    "failures",
    "failedRows",
    "errors",
    "validationErrors",
    "Failures",
  ]).map(normalizeFailure);
  const importedQuestions = arrayValue(source, [
    "importedQuestions",
    "questions",
    "processedQuestions",
    "ImportedQuestions",
  ]);
  const responseIds = arrayValue(source, [
    "importedQuestionIds",
    "questionIds",
    "attachedQuestionIds",
    "ImportedQuestionIds",
  ]);
  const importedQuestionIds = [
    ...new Set([
      ...responseIds,
      ...importedQuestions
        .map((question) => question?.id ?? question?.questionId)
        .filter((id) => id != null),
    ]),
  ];

  return {
    totalRows: numberValue(source, ["totalRows", "totalCount", "rowCount", "TotalRows"]),
    createdCount: numberValue(source, ["createdCount", "created", "CreatedCount"]),
    updatedCount: numberValue(source, ["updatedCount", "updated", "UpdatedCount"]),
    attachedCount: numberValue(source, ["attachedCount", "attached", "AttachedCount"]),
    alreadyAttachedCount: numberValue(source, [
      "alreadyAttachedCount",
      "alreadyAttached",
      "AlreadyAttachedCount",
    ]),
    failedCount:
      numberValue(source, ["failedCount", "failureCount", "FailedCount"]) ||
      failures.length,
    failures,
    importedQuestions,
    importedQuestionIds,
    raw: responseData,
  };
};

const upload = async (url, file, onProgress) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await axios.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      HeaderKey: config.HeaderKey,
    },
    withCredentials: true,
    onUploadProgress: (event) => {
      if (!onProgress || !event.total) return;
      onProgress(Math.round((event.loaded * 100) / event.total));
    },
  });

  return normalizeQuestionImportResult(response.data);
};

export const importQuestions = (file, onProgress) =>
  upload(`${config.apiBaseUrl}/api/Questions/ImportQuestions`, file, onProgress);

export const importQuestionsIntoExam = (examId, file, onProgress) =>
  upload(
    `${config.apiBaseUrl}/api/Exam/ImportQuestionsExcel/${encodeURIComponent(examId)}`,
    file,
    onProgress
  );
